/**
 * Companion BE owns the Lark strings. This file does not paraphrase
 * `LARK_PERSONALITY`. Tesla is out. Product name is Lark — never KAI.
 * Altair is TTS sound only (`spokenName` / `altairIsTtsOnly`).
 *
 * Error-drawer interview: `GET /client-errors/qa-prompt` → `prompt`
 * (`LARK_ERROR_QA_PROMPT` = `LARK_PERSONALITY` + error process gates).
 * That is the interview. Do not reject it as “voice.”
 *
 * `LARK_VOICE_INSTRUCTIONS` = `LARK_PERSONALITY` + cab-log process.
 * Use that only if this tab actually starts a cab voice session.
 * This error drawer does not start a cab session.
 */

export type LarkErrorQaPrompt = {
  prompt: string;
  productName: 'Lark';
  spokenName: string;
  altairIsTtsOnly: boolean;
};

export type LarkVoiceBlock = {
  voiceInstructions: string;
  productName: 'Lark';
  spokenName: string;
  altairIsTtsOnly: boolean;
};

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function spokenMeta(record: Record<string, unknown>): {
  spokenName: string;
  altairIsTtsOnly: boolean;
} {
  return {
    spokenName: asNonEmptyString(record.spokenName) || 'Altair',
    altairIsTtsOnly: record.altairIsTtsOnly !== false,
  };
}

/**
 * Interview system string for the error drawer.
 * Requires `prompt` (`LARK_ERROR_QA_PROMPT`). Cab-log
 * `voiceInstructions` is not a substitute.
 */
export function extractLarkErrorQaPrompt(body: unknown): LarkErrorQaPrompt | null {
  if (!body || typeof body !== 'object') return null;
  const record = body as Record<string, unknown>;
  const prompt = asNonEmptyString(record.prompt);
  if (!prompt) return null;

  return {
    prompt,
    productName: 'Lark',
    ...spokenMeta(record),
  };
}

/**
 * Cab-log voice only. Do not use for the error drawer.
 * Call only if this tab starts a cab voice session.
 */
export function extractLarkVoiceInstructions(body: unknown): LarkVoiceBlock | null {
  if (!body || typeof body !== 'object') return null;
  const record = body as Record<string, unknown>;
  const voice = asNonEmptyString(record.voiceInstructions);
  if (!voice) return null;

  return {
    voiceInstructions: voice,
    productName: 'Lark',
    ...spokenMeta(record),
  };
}
